import React, { useCallback, useState } from 'react';
import { updateIpadByUniqueIdentifier } from '../../core/_requests';
import { APIError, useAxios } from '../../core/_axios';
import { Ipad, IpadSetupToken } from '../../core/_models';
import { useNavigate } from 'react-router-dom';
import ModalAddIpad from '../../components/ModalAddIpad';
import { addIpad } from '../../core/_requests_v2';
import useLocalizedCopy from '../../hooks/useLocalizedCopy';

export default function ManageiPadForm({
    iPad = undefined,
    onSuccessfullyUpdatedIpad
}: {
    iPad?: Ipad
    onSuccessfullyUpdatedIpad?: (iPad: Ipad) => void
}) {
    const axios = useAxios();
    const { copy } = useLocalizedCopy();

    const navigate = useNavigate();

    const [savingIpad, setSavingIpad] = useState(false);
    const [errorSavingIpad, setErrorSavingIpad] = useState<string | null>(null);
    const [successSavingIpad, setSuccessSavingIpad] = useState<string | null>(null);

    const [resultingIpadSetupToken, setResultingIpadSetupToken] = useState<IpadSetupToken | null>(null);

    const onManageIpadFormSubmitted = useCallback(async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (savingIpad) {
            return;
        }
        setSuccessSavingIpad(null);
        setErrorSavingIpad(null);
        setSavingIpad(true);
        const form = event.currentTarget;
        const formData = new FormData(form);
        const data = Object.fromEntries(formData.entries());
        if (!iPad) {
            try {
                const updatedResultingiPadToken = await addIpad(axios, {
                    ...data,
                })
                // Rather than navigating we're going to show the setup token modal...
                // navigate(`/ipads/${resultingiPadToken.ipad.unique_identifier}/setup-device`)
                setResultingIpadSetupToken(updatedResultingiPadToken)
            } catch (exception) {
                console.error(exception)
                const errorMessage = exception instanceof APIError ? exception.message : copy.Common.messages.errorOccurred
                setErrorSavingIpad(errorMessage)
                setSavingIpad(false)
            }
        } else {
            try {
                const resultingiPad = await updateIpadByUniqueIdentifier(axios, iPad.unique_identifier, {
                    ...data,
                })
                if (!onSuccessfullyUpdatedIpad) {
                    throw new Error('onSuccessfullyUpdatedIpad is required to update an iPad')
                }
                onSuccessfullyUpdatedIpad(resultingiPad)
                setSuccessSavingIpad(copy.IPads.manage.updated)
            } catch (exception) {
                console.error(exception)
                const errorMessage = exception instanceof APIError ? exception.message : copy.Common.messages.errorOccurred
                setErrorSavingIpad(errorMessage)
            } finally {
                setSavingIpad(false)
            }
        }
    }, [savingIpad, iPad, axios, onSuccessfullyUpdatedIpad]);

    return <div className="grid grid-cols-1 xl:grid-cols-3 gap-5 lg:gap-7.5">
        <div className="col-span-2">
            <div className="flex flex-col gap-5 lg:gap-7.5">
                <form className="card" onSubmit={onManageIpadFormSubmitted}>
                    <div className="card-header">
                        <h3 className="card-title">
                            {copy.IPads.add.subtitle}
                        </h3>
                    </div>
                    <div className="card-body grid gap-7.5 py-5 lg:py-7.5">
                        <div className="flex flex-col gap-4">
                            <div>
                                <label
                                    htmlFor="ipad_name"
                                    className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                                    {copy.IPads.add.nameLabel} ({copy.Common.labels.required})
                                </label>
                                <input
                                    id="ipad_name"
                                    name="ipad_name"
                                    type="text"
                                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                                    placeholder={copy.IPads.add.namePlaceholder}
                                    required
                                    autoComplete='off'
                                    autoFocus={!iPad}
                                    defaultValue={iPad?.public_data.ipad_name || ''}
                                />
                                <div className="text-sm text-gray-500 dark:text-gray-400 mt-4">
                                    {copy.IPads.add.descriptionPlaceholder}
                                </div>
                            </div>
                        </div>
                        {false && <div className="flex flex-col gap-4">
                            <div>
                                <label
                                    htmlFor="ipad_pin"
                                    className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                                    {`iPad PIN (required)`}
                                </label>
                                <input
                                    id="ipad_pin"
                                    name="ipad_pin"
                                    type="text"
                                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                                    placeholder="1234"
                                    required
                                    autoComplete='off'
                                    defaultValue={iPad?.private_data.ipad_pin || ''}
                                />
                                <div className="text-sm text-gray-500 dark:text-gray-400 mt-4">
                                    Will be used to unlock the iPad. Must be 4 digits.
                                </div>
                            </div>
                        </div>}
                    </div>
                    <div className="card-footer py-3.5 flex flex-col lg:flex-row items-center justify-between">
                        <div>
                            {errorSavingIpad && <div className="text-red-500 text-sm font-medium">{errorSavingIpad}</div>}
                            {successSavingIpad && <div className="text-green-500 text-sm font-medium">{successSavingIpad}</div>}
                        </div>
                        <div>
                            {iPad ? <button
                                type="submit"
                                className="btn btn-sm btn-primary"
                                disabled={savingIpad}
                            >
                                {!savingIpad ? copy.IPads.manage.update : copy.IPads.manage.updating}
                            </button> : <button
                                type="submit"
                                className="btn btn-sm btn-primary"
                                disabled={savingIpad}
                            >
                                {!savingIpad ? copy.IPads.add.addButton : copy.IPads.add.adding}
                            </button>}
                        </div>
                    </div>
                </form>
                {resultingIpadSetupToken && <ModalAddIpad
                    addIpadModalIsOpen={true}
                    setAddIpadModalIsOpen={() => {
                        // we reload to reset the form
                        window.location.reload()
                    }}
                    ipadSetupToken={resultingIpadSetupToken}
                    onIpadConnected={() => {
                        // We navigate to the detail page
                        // after a successful connection
                        //
                        // We assume that a service wants to be sent directly
                        // after creation.
                        navigate(`/ipads/${resultingIpadSetupToken.ipad.unique_identifier}/manage-service`)
                    }}
                />}
            </div>
        </div>
    </div>
}